import React, { FC, useCallback, useContext, useEffect, useState } from 'react';
import { formatDate } from 'utils/format';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
    Box,
    Button,
    Chip,
    IconButton,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useTranslation } from 'react-i18next';
import {
    deleteWebhookDeliveryTemplate,
    getWebhookDeliveriesForOrganizationTemplate,
} from 'apollo/templates/webhook-delivery';
import ConfirmDialog from 'components/shared/confirm-dialog/confirm-dialog.component';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import Loader from 'components/shared/loader/loader.component';
import { WebhookDelivery, WebhookDeliveryList, WebhookDeliveryStatus } from 'models/webhook-delivery';
import { OrganizationRef } from './types';

type StatusFilter = 'ALL' | WebhookDeliveryStatus;

type WebhookDeliveriesProps = {
    organization: OrganizationRef | null;
};

const WebhookDeliveries: FC<WebhookDeliveriesProps> = ({ organization }: WebhookDeliveriesProps) => {
    const { t: translate } = useTranslation(['app-settings', 'common']);
    const { showBoundary } = useDenthubErrorBoundary();
    const { setAppToasterContent } = useContext(AppToasterContext);

    const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [pendingDelete, setPendingDelete] = useState<WebhookDelivery | null>(null);

    const [fetchDeliveries, { data, loading }] = useLazyQuery<{
        webhookDeliveriesForOrganization: WebhookDeliveryList;
    }>(getWebhookDeliveriesForOrganizationTemplate, {
        fetchPolicy: 'network-only',
        onError: showBoundary,
    });

    const runFetch = useCallback(() => {
        if (!organization?._id) return;
        fetchDeliveries({
            variables: {
                organizationId: organization._id,
                filters: statusFilter === 'ALL' ? null : { status: statusFilter },
                limit: rowsPerPage,
                offset: page * rowsPerPage,
            },
        });
    }, [organization, statusFilter, page, rowsPerPage, fetchDeliveries]);

    useEffect(() => {
        runFetch();
    }, [runFetch]);

    // Reset to page 0 whenever the lab or filter changes to avoid landing on
    // an empty page past the end of a smaller result set.
    useEffect(() => {
        setPage(0);
    }, [organization, statusFilter]);

    const [deleteDelivery] = useMutation(deleteWebhookDeliveryTemplate, {
        onCompleted: () => {
            setAppToasterContent({
                isOpen: true,
                severity: 'success',
                message: translate('app-settings:WEBHOOK_DELIVERY_DELETED'),
            });
            runFetch();
        },
        onError: showBoundary,
    });

    const handleConfirmDelete = () => {
        if (!pendingDelete) return;
        deleteDelivery({ variables: { id: pendingDelete._id } });
        setPendingDelete(null);
    };

    const list = data?.webhookDeliveriesForOrganization;
    const items = list?.items ?? [];
    const total = list?.total ?? 0;

    const formatDateOrDash = (iso?: string | null) => (iso ? formatDate(new Date(iso), 'datetime') : '—');

    const statusColor = (status: WebhookDeliveryStatus): 'warning' | 'error' =>
        status === 'DEAD' ? 'error' : 'warning';

    const renderLastError = (delivery: WebhookDelivery) => {
        if (!delivery.lastError) return '—';
        const prefix = delivery.lastStatusCode ? `HTTP ${delivery.lastStatusCode} · ` : '';
        const text = `${prefix}${delivery.lastError}`;
        return (
            <Tooltip title={text}>
                <Typography
                    variant="body2"
                    sx={{
                        maxWidth: 260,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {text}
                </Typography>
            </Tooltip>
        );
    };

    return (
        <Box sx={{ mt: 4 }}>
            <Typography variant="h2" gutterBottom>
                {translate('app-settings:WEBHOOK_DELIVERIES')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {translate('app-settings:WEBHOOK_DELIVERIES_HELP')}
            </Typography>

            <Box display="flex" gap={2} alignItems="center" mb={2}>
                <TextField
                    select
                    size="small"
                    label={translate('app-settings:WEBHOOK_DELIVERIES_FILTER_STATUS')}
                    value={statusFilter}
                    onChange={(event) => setStatusFilter(event.target.value as StatusFilter)}
                    sx={{ minWidth: 160 }}
                    disabled={!organization}
                >
                    <MenuItem value="ALL">{translate('app-settings:WEBHOOK_DELIVERIES_STATUS_ALL')}</MenuItem>
                    <MenuItem value="PENDING">{translate('app-settings:WEBHOOK_DELIVERIES_STATUS_PENDING')}</MenuItem>
                    <MenuItem value="DEAD">{translate('app-settings:WEBHOOK_DELIVERIES_STATUS_DEAD')}</MenuItem>
                </TextField>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={runFetch}
                    disabled={!organization || loading}
                >
                    {translate('app-settings:WEBHOOK_DELIVERIES_REFRESH')}
                </Button>
            </Box>

            {organization && loading && <Loader />}

            {organization && !loading && (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>{translate('app-settings:WEBHOOK_DELIVERIES_EVENT')}</TableCell>
                                <TableCell>{translate('app-settings:WEBHOOK_DELIVERIES_SUBSCRIPTION')}</TableCell>
                                <TableCell>{translate('app-settings:WEBHOOK_DELIVERIES_STATUS')}</TableCell>
                                <TableCell align="right">
                                    {translate('app-settings:WEBHOOK_DELIVERIES_ATTEMPTS')}
                                </TableCell>
                                <TableCell>{translate('app-settings:WEBHOOK_DELIVERIES_NEXT_ATTEMPT')}</TableCell>
                                <TableCell>{translate('app-settings:WEBHOOK_DELIVERIES_LAST_ERROR')}</TableCell>
                                <TableCell>{translate('app-settings:WEBHOOK_DELIVERIES_CREATED')}</TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={8} align="center">
                                        <Typography variant="body2" color="text.secondary">
                                            {translate('app-settings:WEBHOOK_DELIVERIES_EMPTY')}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                            {items.map((delivery) => (
                                <TableRow key={delivery._id}>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {delivery.entity} / {delivery.operation}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            fontFamily="monospace"
                                            sx={{ wordBreak: 'break-all' }}
                                        >
                                            {delivery.entityId}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {delivery.subscriptionName ??
                                                translate('app-settings:WEBHOOK_DELIVERIES_SUBSCRIPTION_MISSING')}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                                            {delivery.subscriptionId}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            label={delivery.status}
                                            color={statusColor(delivery.status)}
                                        />
                                    </TableCell>
                                    <TableCell align="right">{delivery.attempts}</TableCell>
                                    <TableCell>{formatDateOrDash(delivery.nextAttemptAt)}</TableCell>
                                    <TableCell>{renderLastError(delivery)}</TableCell>
                                    <TableCell>{formatDateOrDash(delivery.createdAt)}</TableCell>
                                    <TableCell align="right">
                                        <Tooltip title={translate('common:DELETE')}>
                                            <IconButton size="small" onClick={() => setPendingDelete(delivery)}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        onPageChange={(_event, newPage) => setPage(newPage)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(event) => {
                            setRowsPerPage(Number(event.target.value));
                            setPage(0);
                        }}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                    />
                </TableContainer>
            )}

            <ConfirmDialog
                open={pendingDelete !== null}
                handleClose={() => setPendingDelete(null)}
                handleConfirm={handleConfirmDelete}
                dialogTitle={translate('app-settings:DELETE_WEBHOOK_DELIVERY_CONFIRM_TITLE')}
                dialogContent={
                    pendingDelete
                        ? translate('app-settings:DELETE_WEBHOOK_DELIVERY_CONFIRM_TEXT', {
                              event: `${pendingDelete.entity}/${pendingDelete.operation}`,
                              entityId: pendingDelete.entityId,
                          })
                        : undefined
                }
            />
        </Box>
    );
};

export default WebhookDeliveries;
